/**
 * The single `keydown` listener that turns a chord into a command (§5.9).
 *
 * One listener per window rather than one per feature: the ordering rules below
 * (who gets the key first, what counts as "already claimed") only mean anything
 * if there is exactly one place that decides.
 */

import type { ResolvedCommand, WhenContext } from '@hibro/shared';
import { evaluateWhen } from '@hibro/shared';

import { isCommandEnabled } from '../commands/command-query';
import type { Chord } from './chord';
import { chordMatches, eventToChord, isModifierCode } from './chord';
import type { CompiledKeybinding } from './registry';

/** The parts of a `keydown` the dispatcher judges, beyond the chord itself. */
export interface DispatchKeyEvent {
  readonly code: string;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly metaKey: boolean;
  /** An IME composition is in progress. */
  readonly isComposing: boolean;
  /** Something between the target and here already consumed the key. */
  readonly defaultPrevented: boolean;
  readonly target: EventTarget | null;
  preventDefault: () => void;
}

export interface KeybindingDispatcherOptions {
  executeCommand: (command: string, ...args: unknown[]) => void;
  getContext: () => WhenContext;
  /**
   * Commands, consulted for their `enablement` (架构方案 §5.9). Optional because
   * without a command table there is nothing to evaluate; omitting it leaves
   * every matching chord firing, the behaviour from before `enablement` existed.
   */
  commands?: readonly ResolvedCommand[];
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
}

/**
 * Whether a chord should be ignored because the user is typing into something.
 *
 * Only bare and single-modifier chords are withheld: `Ctrl+Shift+P` is not text
 * a field could have wanted, while a lone `/` or `Alt+S` very much is. Without
 * this, one module binding a plain letter takes that letter away from every
 * input in the app — the failure is remote from its cause and effectively
 * undebuggable from the user's side.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (target.isContentEditable) {
    return true;
  }
  const tag = target.tagName;
  if (tag === 'TEXTAREA') {
    return true;
  }
  if (tag !== 'INPUT') {
    return false;
  }
  // Checkboxes, radios and buttons are `input` elements that consume no text,
  // and a chord over one of them is not competing with anything.
  const type = (target as HTMLInputElement).type;
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range'].includes(
    type,
  );
}

/** Whether a chord carries enough modifiers to be safe over a text field. */
function isTextSafeChord(chord: Chord): boolean {
  const modifiers = [chord.ctrl, chord.alt, chord.meta].filter(Boolean).length;
  return modifiers > 0;
}

/**
 * Install the dispatcher on a window; returns a disposer.
 *
 * A binding fires when its chord matches and every one of these holds: no IME
 * composition is in progress, nothing already claimed the key, the target is not
 * a text field the chord would be stolen from, the binding's own `when` passes,
 * and the target command's `enablement` passes. `when` and `enablement` stay two
 * axes — one gates this binding, the other gates the command everywhere it
 * surfaces — so a chord must not fire an action the command list would refuse.
 */
export function installKeybindingDispatcher(
  bindings: readonly CompiledKeybinding[],
  options: KeybindingDispatcherOptions,
): () => void {
  // A command absent from the table (registered at runtime without a
  // declaration, or simply not supplied) has no `enablement` to fail.
  const enablementOf = new Map(
    (options.commands ?? []).map((command) => [command.id, command.enablement]),
  );
  const target = options.target ?? window;

  const handler = (event: DispatchKeyEvent): void => {
    // The keystroke belongs to the input method: during composition Enter
    // commits, the arrows move through candidates and Escape cancels, none of
    // which the user aimed at this app (同 escape-close 的判据).
    if (event.isComposing) {
      return;
    }
    // Someone closer to the event already acted on it — an overlay dismissing,
    // a widget consuming its own key. Same arbitration as Esc-closes-window
    // (见 08): claiming is spelled `preventDefault`, and there is no list kept
    // here of who might have done it.
    if (event.defaultPrevented) {
      return;
    }
    if (isModifierCode(event.code)) {
      return;
    }
    const eventChord = eventToChord(event);
    const typing = isTypingTarget(event.target);

    // Descending precedence: user edits first, then modules, then built-ins.
    for (let index = bindings.length - 1; index >= 0; index -= 1) {
      const entry = bindings[index];
      if (!entry.chord || !chordMatches(entry.chord, eventChord)) {
        continue;
      }
      if (
        typing &&
        entry.binding.allowInInput !== true &&
        !isTextSafeChord(entry.chord)
      ) {
        continue;
      }
      const context = options.getContext();
      if (
        !evaluateWhen(entry.binding.when, context) ||
        !isCommandEnabled(
          { enablement: enablementOf.get(entry.binding.command) },
          context,
        )
      ) {
        // Keep scanning: another binding on the same chord may be the one whose
        // conditions hold.
        continue;
      }
      event.preventDefault();
      options.executeCommand(
        entry.binding.command,
        ...(entry.binding.args ?? []),
      );
      return;
    }
  };

  target.addEventListener('keydown', handler as unknown as EventListener);
  return () => {
    target.removeEventListener('keydown', handler as unknown as EventListener);
  };
}
